import {
  HoveredShapeStyle,
  SelectedShapeStyle,
  INCHES_IN_ONE_METER,
  PDF_COORDINATE_MAPPING_DPI,
} from '../SheetsViewerV2.config';
import {
  AbstractSheetFilledShape,
  SheetFillStyleSet,
} from './abstract/AbstractSheetFilledShape';
import { LineStyle } from './abstract/AbstractSheetLineShape';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { SheetShapeStyleType } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import { SheetScaleType, SheetShapeDeepFragment } from 'src/gql/graphql';
import Shape = Autodesk.Edit2D.Shape;
import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;
import { ChakraColor } from 'src/utils/color-util';
import { getUnitScaleFromCalibration } from 'src/domain/sheet-calibration';

export class PointShape extends AbstractSheetFilledShape {
  static Radius = 0.1;

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    edit2d: Edit2D,
    activeSheetData: ActiveSheetData,
    calibration?: SheetScaleType,
    shapeModel?: SheetShapeDeepFragment,
    point?: THREE.Vector3,
    styleSet?: SheetFillStyleSet,
    isProtan?: boolean
  ) {
    super(viewer, edit2d, activeSheetData, shapeModel);

    const convertedPoint = this.validateAndConvertPoint(shapeModel, point);

    if (isProtan && calibration) {
      this.createProtanShapes(convertedPoint, calibration);
    } else {
      this.createStandardShapes(convertedPoint);
    }

    this.textLabelPoints = !isProtan ? [convertedPoint] : [];
    this.styleSet = styleSet || null;
    this.setStyle(SheetShapeStyleType.default);
  }

  private validateAndConvertPoint(
    shapeModel?: SheetShapeDeepFragment,
    point?: THREE.Vector3
  ): THREE.Vector3 {
    const convertedPoint =
      shapeModel && shapeModel.sheetShapePoint
        ? this.convertPointToRender(shapeModel.sheetShapePoint.point)
        : point;

    if (shapeModel && !shapeModel.sheetShapePoint) {
      throw new Error('shapeModel.sheetShapePoint must be defined');
    } else if (!shapeModel && !point) {
      throw new Error('One shapeModel or point must be passed');
    } else if (!convertedPoint) {
      throw new Error('Cannot define point');
    }

    return convertedPoint as THREE.Vector3;
  }

  private createProtanShapes(
    convertedPoint: THREE.Vector3,
    calibration: SheetScaleType
  ): void {
    const unitScale = getUnitScaleFromCalibration(calibration);

    // Get the viewer's internal unit scale - this is what ApiCoordinatesConverter uses
    const viewerUnitScale = this.viewer.model?.getUnitScale() || 1;

    // Calculate the full scale factor that ApiCoordinatesConverter applies
    const apiConverterScale =
      (INCHES_IN_ONE_METER * viewerUnitScale) / PDF_COORDINATE_MAPPING_DPI;

    // Convert 350mm to the same coordinate system as the converted point
    const rectangleSize = (350 / 1000 / unitScale) * apiConverterScale; // 350mm in viewer coordinates
    const circleSize = rectangleSize / 4;

    // Create the rectangle as a polygon
    const halfSize = rectangleSize / 2;
    const rectanglePoints = [
      new THREE.Vector3(
        convertedPoint.x - halfSize,
        convertedPoint.y - halfSize,
        0
      ),
      new THREE.Vector3(
        convertedPoint.x + halfSize,
        convertedPoint.y - halfSize,
        0
      ),
      new THREE.Vector3(
        convertedPoint.x + halfSize,
        convertedPoint.y + halfSize,
        0
      ),
      new THREE.Vector3(
        convertedPoint.x - halfSize,
        convertedPoint.y + halfSize,
        0
      ),
      new THREE.Vector3(
        convertedPoint.x - halfSize,
        convertedPoint.y - halfSize,
        0
      ), // Close the polygon
    ];

    // Create the shapes
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const rectangle = new Autodesk.Edit2D.Polygon(rectanglePoints);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const circle = new Autodesk.Edit2D.Circle(
      convertedPoint.x,
      convertedPoint.y,
      circleSize
    );

    // Add both shapes to the shapes array
    this.shapes = [rectangle, circle];

    // Apply hard-coded colors for Protan shapes
    const styleSet: SheetFillStyleSet = {
      default: {
        lineWidth: 2,
        fillAlpha: 0.3,
        lineColor: ChakraColor.gray800,
        fillColor: ChakraColor.gray400,
        lineStyle: LineStyle.Solid,
      },
      hovered: {
        ...HoveredShapeStyle,
        lineWidth: 2,
        lineColor: ChakraColor.gray300,
      },
      selected: {
        ...SelectedShapeStyle,
        lineWidth: 3,
      },
    };

    // Apply the custom styles to each shape
    this.setShapeStyle(rectangle, styleSet);
    this.setShapeStyle(circle, styleSet);
  }

  private createStandardShapes(convertedPoint: THREE.Vector3): void {
    // Standard rendering for non-Protan tenants
    this.shapes = [
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      new Autodesk.Edit2D.Circle(
        convertedPoint.x,
        convertedPoint.y,
        PointShape.Radius
      ),
    ];
  }

  public hitTest(x: number, y: number) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.shapes.some((shape) => shape.hitTest(x, y, 0.1));
  }
}
